import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Edit3, Trash2, ArrowLeft, RefreshCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminArticles() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/articles");
      setArticles(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-articles" className="container-app py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin" className="text-xs section-eyebrow hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Admin</Link>
          <h1 className="font-display text-4xl text-navy mt-2">Articles / Blog CMS</h1>
          <div className="text-sm text-slate-500">Signed in as {user?.email}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-outline"><RefreshCcw className="w-4 h-4" /> Refresh</button>
          <Link to="/admin/articles/new" data-testid="new-article-btn" className="btn-gold"><Plus className="w-4 h-4" /> New article</Link>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>
      ) : (
        <div className="mt-8 border border-slate-200 rounded-sm bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest">Title</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest">Slug</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest">Tags</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest">Status</th>
                <th className="p-3 font-mono text-[10px] uppercase tracking-widest">Updated</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {articles.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500 text-sm">No articles yet.</td></tr>
              )}
              {articles.map((a) => (
                <tr key={a.id} className="border-t border-slate-100" data-testid={`article-row-${a.slug}`}>
                  <td className="p-3 text-navy font-medium">{a.title}</td>
                  <td className="p-3 font-mono text-xs text-slate-500">/blog/{a.slug}</td>
                  <td className="p-3 text-xs text-slate-600">{(a.tags || []).join(", ")}</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${a.status === "published" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-slate-500">{a.updated_at ? new Date(a.updated_at).toLocaleDateString("en-IN") : "—"}</td>
                  <td className="p-3 flex gap-2 justify-end">
                    <Link to={`/admin/articles/${a.id}`} data-testid={`edit-${a.slug}`} className="btn-outline !py-1 !px-2 !text-xs"><Edit3 className="w-3 h-3" /> Edit</Link>
                    <button onClick={() => remove(a)} data-testid={`delete-${a.slug}`} className="btn-outline !py-1 !px-2 !text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
